/**
 * Download Modal Component
 * Popup form for user to enter name and email before downloading eBook
 */

import React, { useState } from 'react';
import { recordDownload } from '../api';

const DownloadModal = ({ isOpen, onClose, ebook, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadReady, setDownloadReady] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setError(''); // Clear error when user starts typing
  };

  // Validate email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Convert Google Drive link to direct download format
  const getDirectDownloadUrl = (url) => {
    if (!url) return '';
    
    if (url.includes('drive.google.com')) {
      const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) || url.match(/id=([a-zA-Z0-9_-]+)/);
      if (fileIdMatch && fileIdMatch[1]) {
        return `https://drive.google.com/uc?export=download&id=${fileIdMatch[1]}`;
      }
    }
    return url;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!formData.email.trim()) {
      setError('Please enter your email');
      return;
    }

    if (!isValidEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);

      // Send download data to backend
      await recordDownload({
        name: formData.name,
        email: formData.email,
        ebookId: ebook.id,
      });

      // Trigger auto-download
      const directUrl = getDirectDownloadUrl(ebook.downloadLink);
      const link = document.createElement('a');
      link.href = directUrl;
      link.download = '';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Call success callback
      onSuccess(ebook.downloadLink, ebook.id);

      // Show download success state
      setDownloadReady(true);
      setError('');
    } catch (err) {
      // Handle specific error messages from backend
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to process download. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close and reset
  const handleClose = () => {
    setDownloadReady(false);
    setFormData({ name: '', email: '' });
    setError('');
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal active">
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">
            {downloadReady ? '✅ Download Ready' : '📥 Download Magazine'}
          </h2>
          <button
            className="close-btn"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Success State */}
        {downloadReady ? (
          <div className="text-center">
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
            <h3 style={{ color: '#28a745', marginBottom: '1rem' }}>Downloaded Successfully!</h3>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              Your Magazine has been downloaded to your device.
            </p>
            <button
              type="button"
              className="btn"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* eBook Info */}
            <div className="mb-3">
              <p className="text-center">
                <strong>📚 {ebook?.title}</strong>
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="admin-form">
              {/* Name Field */}
              <div className="form-group">
                <label htmlFor="name">Your Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  disabled={loading}
                />
              </div>

              {/* Email Field */}
              <div className="form-group">
                <label htmlFor="email">Your Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  disabled={loading}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="form-group">
                  <div className="error-message">{error}</div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="btn"
                disabled={loading}
              >
                {loading ? (
                  <div className="spinner"></div>
                ) : (
                  '⬇️ Download PDF'
                )}
              </button>

              {/* Close Button */}
              <button
                type="button"
                className="btn btn-secondary mt-2"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
            </form>

            <p className="text-center mt-2" style={{ fontSize: '0.85rem', color: '#999' }}>
              Your information is secure and will be stored in our database.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default DownloadModal;
